// Simple logging utility for the application
// Logs are sent to console in development and can be extended to send to a logging service

const LOG_LEVELS = {
    DEBUG: 'DEBUG',
    INFO: 'INFO',
    WARN: 'WARN',
    ERROR: 'ERROR',
    SECURITY: 'SECURITY'
};

class Logger {
    constructor() {
        this.isDevelopment = import.meta.env.DEV;
    }

    _formatMessage(level, message, data = {}) {
        return {
            timestamp: new Date().toISOString(),
            level,
            message,
            ...data
        };
    }

    _log(level, message, data = {}) {
        const logEntry = this._formatMessage(level, message, data);

        if (this.isDevelopment) {
            console.log(`[${logEntry.level}] ${logEntry.timestamp}:`, logEntry.message, data);
        }

        // TODO: In production, send to logging service (e.g., Sentry, LogRocket)
        // this._sendToLoggingService(logEntry);
    }

    debug(message, data = {}) {
        if (this.isDevelopment) {
            this._log(LOG_LEVELS.DEBUG, message, data);
        }
    }

    info(message, data = {}) {
        this._log(LOG_LEVELS.INFO, message, data);
    }

    warn(message, data = {}) {
        this._log(LOG_LEVELS.WARN, message, data);
    }

    error(message, error, data = {}) {
        this._log(LOG_LEVELS.ERROR, message, {
            ...data,
            error: error?.message,
            stack: error?.stack
        });
    }

    security(message, data = {}) {
        this._log(LOG_LEVELS.SECURITY, message, data);
        // Security logs should always be sent to monitoring service
        // this._sendToSecurityMonitoring(this._formatMessage(LOG_LEVELS.SECURITY, message, data));
    }

    // Authentication events
    authLogin(userId, method = 'google') {
        this.security('User login', { userId, method });
    }

    authLogout(userId) {
        this.security('User logout', { userId });
    }

    authFailed(reason) {
        this.security('Authentication failed', { reason });
    }

    // Data mutation events
    dataCreate(collection, docId, userId) {
        this.info('Document created', { collection, docId, userId });
    }

    dataUpdate(collection, docId, userId) {
        this.info('Document updated', { collection, docId, userId });
    }

    dataDelete(collection, docId, userId) {
        this.warn('Document deleted', { collection, docId, userId });
    }

    // Performance monitoring
    performanceStart(operation) {
        return {
            operation,
            startTime: performance.now()
        };
    }

    performanceEnd(tracker) {
        const duration = performance.now() - tracker.startTime;
        this.info('Performance metric', {
            operation: tracker.operation,
            duration: `${duration.toFixed(2)}ms`
        });
    }
}

export default new Logger();
